import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/store';
import { LayoutGrid, Lock, Loader2, Mail } from 'lucide-react';
import { PLATFORM, BASE, allApps, platformExchangeError, platformSignIn } from '../lib/platform';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(platformExchangeError);
  const [loading, setLoading] = useState(false);
  // Under the platform people sign in there, unless its sign-in just failed.
  const toPlatform = PLATFORM && !platformExchangeError();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (toPlatform) {
      const r = params.get('redirect') || '/';
      platformSignIn(r.startsWith('/') && !r.startsWith('//') ? r : '/');
    } else if (params.get('session') === 'expired') {
      setError('Your session expired. Please log in again.');
    }
  }, [toPlatform]);

  if (toPlatform) return null;

  // Under the platform people have no Quotations password, so after a failed
  // platform sign-in (account switched off, expired pass) there is nothing to
  // type here: show why, and the way back to the other apps.
  if (PLATFORM) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-heda-cream px-4" data-testid="login-page">
        <div className="w-full max-w-sm rounded-3xl border border-heda-line bg-white p-7 text-center shadow-heda sm:p-8">
          <img src={`${BASE}/heda-logo.png`} alt="HEDA PLYWOODS" className="mx-auto h-20 w-auto" data-testid="login-logo" />
          <h1 className="mt-4 font-display text-2xl font-bold text-heda-ink">Hedaply Quotations</h1>
          <div className="heda-error mt-5 text-left" data-testid="login-error">{error}</div>
          <button type="button" onClick={allApps} className="heda-btn mt-5 w-full" data-testid="login-all-apps">
            <LayoutGrid size={14} /> Back to all apps
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-heda-cream px-4" data-testid="login-page">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded-3xl border border-heda-line bg-white p-7 shadow-heda sm:p-8"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={`${BASE}/heda-logo.png`} alt="HEDA PLYWOODS" className="h-20 w-auto" data-testid="login-logo" />
          <h1 className="mt-4 font-display text-2xl font-bold text-heda-ink">Hedaply Quotations</h1>
          <p className="mt-1 text-[13px] text-heda-mute">Smart Quotation System</p>
        </div>

        <label htmlFor="email" className="heda-label">Email</label>
        <div className="relative mb-4">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="heda-input pl-9"
            data-testid="login-email-input"
            required
          />
        </div>

        <label htmlFor="password" className="heda-label">Password</label>
        <div className="relative">
          <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="heda-input pl-9"
            data-testid="login-password-input"
            required
          />
        </div>

        {error && (
          <div className="heda-error mt-3" data-testid="login-error">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading || !email || !password}
          className="heda-btn mt-5 w-full"
          data-testid="login-submit-button"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
          {loading ? 'Signing in...' : 'Sign In'}
        </button>

        <p className="mt-5 text-center text-[11.5px] text-heda-mute">
          HEDA Plywoods · Authorised users only
        </p>
      </form>
    </div>
  );
}
